import { useNavigate } from "react-router-dom";
import { $crud } from "../utils/CrudFactory";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.extend(duration);

export const retrieveData = async (url, params = {}) => {
  const { data } = await $crud.retrieve(url, params);
  return data;
};

export function useNotiNav() {
  const navigate = useNavigate();

  function handleNotificationNav(obj) {
    switch (obj.type) {
      case "RegularOffer":
        return navigate("/services/regular-offers", {
          state: { id: obj.params.regularOfferId },
        });
      case "HelpOffer":
        return navigate("/services/help-offers", {
          state: { id: obj.params.helpOfferId },
        });

      case "Order":
        return navigate(`/order-detail`, {
          state: { selectedOrder: obj.params.order },
        });

      case "Client":
        return navigate(`/client-detail`, {
          state: { selectedClient: obj.params.client },
        });

      case "Driver":
        return navigate(`/driver-detail`, {
          state: { selectedDriver: obj.params.driver },
        });

      default:
        return;
    }
  }

  return { handleNotificationNav };
}

export function toLocalTime(date, format) {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dateTime = dayjs.utc(date).tz(userTimeZone);

  return dateTime.format(format || "DD MMM YYYY");
}

export function timetoUTC(date) {
  if (!dayjs(date).isValid()) return date;

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const toUTC = dayjs.tz(date, userTimeZone).utc().toISOString();

  return toUTC;
}

export function toUTCTime(date) {
  return new Date(
    new Date(date).getTime() - new Date(date).getTimezoneOffset() * 60000,
  );
}

export const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "CAD",
});

export function timeAgo(dateInput) {
  const now = new Date();
  const date = new Date(dateInput);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) {
    return `${seconds <= 1 ? 1 : seconds} sec ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }

  const weeks = Math.floor(days / 7);
  if (weeks < 4) {
    return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} month${months > 1 ? "s" : ""} ago`;
  }

  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? "s" : ""} ago`;
}

export function timeOnline(lastOnline, lastOffline) {
  if (!lastOnline || !lastOffline) return "N/A";

  const start = dayjs(lastOnline);
  const end = dayjs(lastOffline);

  // precise difference in seconds (handles fractional milliseconds)
  const diffSeconds = Math.floor(end.diff(start, "second", true));

  if (diffSeconds < 60) {
    return `${diffSeconds} sec`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} min${diffMinutes > 1 && "s"}`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hr${diffHours > 1 && "s"}`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 && "s"}`;
  }

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) {
    return `${diffWeeks} week${diffWeeks > 1 && "s"}`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths > 1 && "s"}`;
  }

  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears} year${diffYears > 1 && "s"}`;
}

let navigator;

export const setNavigator = (nav) => {
  navigator = nav;
};

export const navigateTo = (path) => {
  navigator?.(path);
};

export async function encryptPassword(password, publicKeyPem) {
  const pemHeader = "-----BEGIN PUBLIC KEY-----";
  const pemFooter = "-----END PUBLIC KEY-----";

  const pemContents = publicKeyPem
    .replace(pemHeader, "")
    .replace(pemFooter, "")
    .replace(/\s/g, "");

  const binaryDer = Uint8Array.from(atob(pemContents), (c) => c.charCodeAt(0));

  const publicKey = await window.crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"],
  );

  const encrypted = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    new TextEncoder().encode(password),
  );

  return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
}

export const formatMoney = (amount, currency = "CAD") => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    currency,
  }).format(Number(amount) || 0);
};
